import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = apiErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2"
      style={{ background: "var(--content-bg)" }}
      data-testid="login-page"
    >
      {/* Left — navy brand panel */}
      <div
        className="relative overflow-hidden flex flex-col justify-between px-8 py-10 lg:px-16 lg:py-14 order-2 lg:order-1"
        style={{ background: "var(--sidebar-bg)", color: "var(--sidebar-fg)" }}
      >
        {/* Subtle brand-orange radial accent */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(232,67,26,0.45), rgba(232,67,26,0) 70%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(24,43,92,0.9), rgba(13,27,61,0) 70%)",
          }}
        />

        <div className="relative flex items-center gap-2 text-sm text-slate-300">
          <ShieldCheck className="h-4 w-4" />
          <span className="uppercase tracking-widest text-[11px]">
            Internal · Post-Sales OS
          </span>
        </div>

        <div className="relative flex-1 flex flex-col justify-center max-w-md">
          <BrandMark variant="light" size="lg" testId="login-brandmark" />
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">
              Pranava Group
            </div>
            <h2 className="font-heading text-3xl font-semibold leading-tight text-white">
              Booking to Handover.
              <br />
              <span style={{ color: "#F79C77" }}>One connected journey.</span>
            </h2>
            <p className="mt-4 text-sm text-slate-300 max-w-sm">
              Sales, CRM, Accounts, Banking, Legal, Registration, Site, QA and
              Handover — server-enforced RBAC, one audit trail, one operating
              picture.
            </p>
          </div>
        </div>

        <div className="relative text-[11px] text-slate-400 tracking-wide">
          © Pranava Group · Post-Sales OS
        </div>
      </div>

      {/* Right — form on warm off-white */}
      <div
        className="flex flex-col justify-between px-6 py-10 lg:px-16 order-1 lg:order-2"
        style={{ background: "var(--content-bg)" }}
      >
        <div className="lg:hidden flex items-center gap-2">
          <BrandMark variant="dark" size="sm" />
        </div>

        <div className="max-w-md mx-auto w-full">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-warm-100 bg-white px-2.5 py-0.5 text-[11px] font-medium tracking-wide uppercase text-slate-600">
              <span
                className="inline-flex h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--brand)" }}
              />
              Sign in to HomeFlow
            </div>
            <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-slate-900">
              Welcome back.
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Your operating picture is waiting.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@pranava.local"
                required
                className="h-11 bg-white border-warm-100"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 bg-white border-warm-100"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 text-white font-medium"
              style={{ background: "var(--brand)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--brand-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand)")}
              data-testid="login-submit-button"
            >
              <KeyRound className="h-4 w-4" />
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div
            className="mt-6 rounded-lg border border-warm-100 bg-white p-4 text-xs text-slate-600"
            data-testid="login-hint"
          >
            <div className="font-medium text-slate-800 mb-1.5 flex items-center gap-1.5">
              <span
                className="inline-flex h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--brand)" }}
              />
              Seed accounts
            </div>
            <div>
              Super Admin — <code className="font-mono">admin@pranava.local</code> /{" "}
              <code className="font-mono">Admin@123</code>
            </div>
            <div>
              Any role user — <code className="font-mono">&lt;role&gt;@pranava.local</code> /{" "}
              <code className="font-mono">Pass@123</code>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-400 mt-6 lg:mt-0">
          Audit-logged · Server-enforced RBAC · JWT
        </div>
      </div>
    </div>
  );
}
